import { type FC, type ReactNode, useMemo } from 'react';

import {
  type ClientRuntimeEnvironment,
  DefaultClientRuntimeEnvironment,
  type PublicPageEnv,
} from '@/src/environment';

import { EnvironmentContextObj } from '../context';
import { useApiClientEnvironment } from '../hooks/use-api-client-environment';

export interface EnvironmentProviderProps {
  readonly children: ReactNode;
  /**
   * The server-parsed environment injected through the page's server-side props (the `env` field
   * of the `auth` page prop, provided by the page-context wrappers). Non-public pages inject a
   * superset of {@link PublicPageEnv}; only the base subset is exposed through this provider.
   * When {@link null}, as on the NextJS error pages which cannot run `getServerSideProps`, the
   * provider falls back to fetching `/api/client-environment`.
   */
  readonly environment?: null | PublicPageEnv;
}

/**
 * Provides the {@link ClientRuntimeEnvironment} to the application through
 * {@link EnvironmentContextObj}, consumed via `useClientEnvironment`.
 *
 * A server-injected environment is authoritative whenever present: the page-context wrappers
 * guarantee its validity (an unparsable environment fails the page request instead of rendering),
 * so it is exposed as ready with no client-side request. Only when no environment was injected
 * does the provider fall back to the `/api/client-environment` request, performed non-blocking
 * through SWR while consumers observe the not-ready shape.
 *
 * In practice, only the NextJS error pages (`/404` and `/500`) render without an injected
 * environment: those are the public pages that deliberately remain statically generated, since
 * adding `getServerSideProps` to them would put a server render behind every stray 404 request.
 * Every other public page injects the environment through `publicPageContext`, and Auth0 and LTI
 * pages always inject it through their own context wrappers, so reaching the fallback request on
 * any page other than an error page indicates a page missing its wrapper.
 */
export const EnvironmentProvider: FC<EnvironmentProviderProps> = ({
  children,
  environment = null,
}) => {
  const data = useApiClientEnvironment({ isEnabled: environment === null });

  const value = useMemo<ClientRuntimeEnvironment>(() => {
    if (environment !== null) {
      return { env: environment, isReady: true };
    }
    if (data) {
      return { env: data, isReady: true };
    }
    return DefaultClientRuntimeEnvironment;
  }, [environment, data]);

  return <EnvironmentContextObj value={value}>{children}</EnvironmentContextObj>;
};
